// NewProject.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                onClick={() => setOpen(!open)}>
                <span>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

const STATUSES = ["Planning", "Active", "On Hold", "Completed", "Archived", "Cancelled"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const VISIBILITIES = ["Private", "Internal", "Public"];
const PROJECT_TYPES = ["Internal", "Client", "Research", "Maintenance", "Compliance", "Other"];
const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const ACCENT_COLORS = ["#1e3a5f", "#2d6a4f", "#6d3b7d", "#a0522d", "#b5451b", "#1a5276", "#117a65", "#145a32", "#4a235a", "#784212"];

const initialForm = {
    name: "", key: "", description: "", icon: "📁", accentColor: "#1e3a5f",
    category: "", industry: "", projectType: "Internal", customer: "",
    owner: "", status: "Planning", priority: "Medium", visibility: "Private",
    startDate: "", endDate: "", estimatedBudget: "", budgetCurrency: "INR",
    referenceNumber: "", tags: [], sprintDuration: 14,
};

export default function NewProject() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialForm);
    const [customers, setCustomers] = useState([]);
    const [ownerEmployees, setOwnerEmployees] = useState([]);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [tagInput, setTagInput] = useState("");
    const [activeTab, setActiveTab] = useState("governance");
    const [navbarColor, setNavbarColor] = useState("#1e3a5f");
    const token = localStorage.getItem("token");
    const refs = useRef({});
    const setRef = (name) => (el) => { refs.current[name] = el; };

    // Bootstrap loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Navbar color from user appearance settings
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => { });
    }, [token]);

    useEffect(() => {
        const navbar = document.querySelector(".navbar");
        if (navbar) navbar.style.backgroundColor = navbarColor;
    }, [navbarColor]);

    // Auth check
    useEffect(() => {
        const token = localStorage.getItem("token");
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }
    }, [navigate]);

    // Fetch customers
    useEffect(() => {
        const token = localStorage.getItem("token");
        fetch(`${API_BASE_URL}/api/customers/all?status=Active&size=200`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.json())
            .then((d) => setCustomers(Array.isArray(d) ? d : d.content || []))
            .catch(() => { });
    }, []);

    // Fetch owner employees summary
    useEffect(() => {
        const token = localStorage.getItem("token");
        fetch(`${API_BASE_URL}/api/employees/active/summary`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => r.json())
            .then((d) => setOwnerEmployees(Array.isArray(d) ? d : []))
            .catch(() => { });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setTouched((t) => ({ ...t, [name]: true }));
        if (name === "name" && !touched.key) {
            setFormData((prev) => ({
                ...prev,
                name: value,
                key: value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10),
            }));
        }
    };

    const addTag = (val) => {
        const t = val.trim();
        if (t && !formData.tags.includes(t)) setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
        setTagInput("");
    };

    const removeTag = (tag) =>
        setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));

    const inputClass = (field) => {
        if (errors[field]) return "form-control is-invalid";
        if (touched[field] && !errors[field]) return "form-control is-valid";
        return "form-control";
    };

    const selectClass = (field) => {
        if (errors[field]) return "form-select is-invalid";
        if (touched[field] && !errors[field]) return "form-select is-valid";
        return "form-select";
    };

    const validate = () => {
        const e = {};
        if (!formData.name.trim()) e.name = "Project name is required.";
        if (!formData.key.trim()) e.key = "Project key is required.";
        if (!formData.projectType) e.projectType = "Project type is required.";
        if (!formData.status) e.status = "Status is required.";
        if (formData.projectType === "Client" && !formData.customer) e.customer = "Customer is required for Client projects.";
        if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) e.endDate = "End date must be after start date.";
        if (formData.estimatedBudget && isNaN(Number(formData.estimatedBudget))) e.estimatedBudget = "Budget must be a number.";
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        setErrors(newErrors);
        const touchedAll = {};
        Object.keys(initialForm).forEach((k) => (touchedAll[k] = true));
        setTouched(touchedAll);
        if (Object.keys(newErrors).length > 0) { toast.error("Please fix form errors before submitting."); return; }
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/api/projects`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error(await res.text());
            toast.success("Project created successfully!", { onClose: () => navigate("/allprojects") });
            setFormData(initialForm);
            setErrors({});
            setTouched({});
        } catch (err) {
            toast.error(err.message || "Failed to create project.");
        }
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="fixed-top"><AdminNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
                <div className="mb-3">
                    <h4 className="m-0"><i className="bi bi-kanban me-2"></i> New Project</h4>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <button type="submit" form="projectForm" className="btn btn-primary me-2">Save</button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                        </div>
                        <a href="/allprojects" className="text-primary text-decoration-none me-3">List</a>
                    </div>
                </div>

                <form id="projectForm" onSubmit={handleSubmit}>

                    {/* Basic Information */}
                    <Section title="Basic Information">
                        <div className="col-md-3">
                            <label className="form-label">Project Name <span className="text-danger">*</span></label>
                            <input ref={setRef("name")} type="text" name="name" className={inputClass("name")}
                                value={formData.name} onChange={handleChange} placeholder="e.g. HRMS Phase 2" />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Project Key <span className="text-danger">*</span></label>
                            <input ref={setRef("key")} type="text" name="key" className={inputClass("key")}
                                value={formData.key}
                                onChange={(e) => {
                                    setFormData((p) => ({ ...p, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) }));
                                    setTouched((t) => ({ ...t, key: true }));
                                }}
                                placeholder="e.g. HRMS" />
                            {errors.key && <div className="invalid-feedback">{errors.key}</div>}
                            <div className="form-text">Up to 10 chars — auto-generated from name</div>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Icon (Emoji)</label>
                            <input type="text" name="icon" className="form-control" value={formData.icon}
                                onChange={handleChange} maxLength={2} placeholder="e.g. 🚀" />
                            <div className="form-text">Single emoji as project avatar</div>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Accent Color</label>
                            <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                                {ACCENT_COLORS.map((c) => (
                                    <div key={c} onClick={() => setFormData((p) => ({ ...p, accentColor: c }))}
                                        style={{
                                            width: 24, height: 24, borderRadius: 4, background: c, cursor: "pointer",
                                            border: formData.accentColor === c ? "2px solid #222" : "2px solid transparent"
                                        }} />
                                ))}
                                <input type="text" className="form-control form-control-sm" style={{ width: 90 }}
                                    value={formData.accentColor}
                                    onChange={(e) => setFormData((p) => ({ ...p, accentColor: e.target.value }))} />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <textarea name="description" className="form-control" rows={3}
                                value={formData.description} onChange={handleChange}
                                placeholder="Rich text description of the project scope…" />
                        </div>
                    </Section>

                    {/* Classification */}
                    <Section title="Classification">
                        <div className="col-md-3">
                            <label className="form-label">Project Type <span className="text-danger">*</span></label>
                            <select ref={setRef("projectType")} name="projectType" className={selectClass("projectType")}
                                value={formData.projectType} onChange={handleChange}>
                                {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                            {errors.projectType && <div className="invalid-feedback">{errors.projectType}</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Industry</label>
                            <select name="industry" className="form-select" value={formData.industry} onChange={handleChange}>
                                <option value="">Select industry…</option>
                                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Category</label>
                            <input type="text" name="category" className="form-control" value={formData.category}
                                onChange={handleChange} placeholder="e.g. Engineering, Marketing" />
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">
                                Customer {formData.projectType === "Client" && <span className="text-danger">*</span>}
                            </label>
                            <select ref={setRef("customer")} name="customer" className={selectClass("customer")}
                                value={formData.customer} onChange={handleChange}>
                                <option value="">Select customer…</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                            </select>
                            {errors.customer && <div className="invalid-feedback">{errors.customer}</div>}
                            {formData.projectType === "Client" && <div className="form-text">Required when Project Type is Client</div>}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Owner</label>
                            <select name="owner" className="form-select" value={formData.owner} onChange={handleChange}>
                                <option value="">Select owner…</option>
                                {ownerEmployees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                                ))}
                            </select>
                            <div className="form-text">Defaults to creator if left blank</div>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Reference Number</label>
                            <input type="text" name="referenceNumber" className="form-control"
                                value={formData.referenceNumber} onChange={handleChange}
                                placeholder="PO / Contract / Case no." />
                        </div>
                    </Section>

                </form>

                {/* Tab bar */}
                <ul className="nav nav-tabs mt-3 mb-0" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["governance", "Status & Governance"],
                        ["timeline", "Timeline"],
                        ["budget", "Budget"],
                        ["tags", "Tags"],
                    ].map(([key, label]) => (
                        <li className="nav-item" key={key}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === key ? "active" : ""}`}
                                onClick={() => setActiveTab(key)}
                                style={{
                                    color: activeTab === key ? navbarColor : "#fff",
                                    backgroundColor: activeTab === key ? "#fff" : "transparent",
                                    fontWeight: activeTab === key ? "600" : "normal",
                                }}
                            >
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="p-3 border border-top-0 bg-white">
                    {/* Status & Governance */}
                    {activeTab === "governance" && (
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Status <span className="text-danger">*</span></label>
                                <select ref={setRef("status")} name="status" className={selectClass("status")}
                                    value={formData.status} onChange={handleChange}>
                                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                                {errors.status && <div className="invalid-feedback">{errors.status}</div>}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Priority</label>
                                <select name="priority" className="form-select" value={formData.priority} onChange={handleChange}>
                                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Visibility</label>
                                <select name="visibility" className="form-select" value={formData.visibility} onChange={handleChange}>
                                    {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    {activeTab === "timeline" && (
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Start Date</label>
                                <input ref={setRef("startDate")} type="date" name="startDate"
                                    className={inputClass("startDate")} value={formData.startDate} onChange={handleChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">End Date</label>
                                <input ref={setRef("endDate")} type="date" name="endDate"
                                    className={inputClass("endDate")} value={formData.endDate}
                                    min={formData.startDate || ""} onChange={handleChange} />
                                {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                                <div className="form-text">Must be after start date</div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Sprint Duration (days)</label>
                                <input type="number" name="sprintDuration" className="form-control"
                                    value={formData.sprintDuration} onChange={handleChange}
                                    min={1} max={90} placeholder="14" />
                                <div className="form-text">Auto-sprint length when sprint expires</div>
                            </div>
                        </div>
                    )}

                    {/* Budget */}
                    {activeTab === "budget" && (
                        <div className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Estimated Budget</label>
                                <input ref={setRef("estimatedBudget")} type="number" name="estimatedBudget"
                                    className={inputClass("estimatedBudget")} value={formData.estimatedBudget}
                                    onChange={handleChange} placeholder="0.00" step="0.01" min="0" />
                                {errors.estimatedBudget && <div className="invalid-feedback">{errors.estimatedBudget}</div>}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Budget Currency</label>
                                <select name="budgetCurrency" className="form-select" value={formData.budgetCurrency} onChange={handleChange}>
                                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Tags */}
                    {activeTab === "tags" && (
                        <div className="row g-3">
                            <div className="col-md-12">
                                <label className="form-label">Tags</label>
                                <div className="border rounded p-2 d-flex flex-wrap gap-2 align-items-center"
                                    style={{ minHeight: 42, background: "#fafaf9" }}>
                                    {formData.tags.map((t) => (
                                        <span key={t} className="badge"
                                            style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontSize: 12 }}>
                                            {t}
                                            <button type="button" className="btn-close ms-1" style={{ fontSize: 8 }} onClick={() => removeTag(t)} />
                                        </span>
                                    ))}
                                    <input type="text" className="border-0 flex-grow-1"
                                        style={{ outline: "none", minWidth: 100, background: "transparent", fontSize: 13 }}
                                        placeholder={formData.tags.length ? "" : "Add tag, press Enter…"}
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
                                            if (e.key === "Backspace" && !tagInput && formData.tags.length) {
                                                setFormData((p) => ({ ...p, tags: p.tags.slice(0, -1) }));
                                            }
                                        }}
                                        onBlur={() => tagInput.trim() && addTag(tagInput)} />
                                </div>
                                <div className="form-text">Press Enter or comma to add tags</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center mt-2 mb-4">
                    <button type="submit" form="projectForm" className="btn btn-primary me-2">Save</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                </div>
            </div>
        </>
    );
}